import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'

import { eSignDocumentsApi, legalDocumentsStateApi, previewLegalDocumentsApi } from 'core'

import { Button, Notification, Skeleton } from 'common/components'
import { useCommandRequestV2, useLoginUserV2, useQueryParams, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import './ESignStyles.less'

export const SIGN_PLAN_DOCUMENTS_PATH = 'sign-documents'

type EsignPlanDocumentsProps = {}

const ESignPlanDocuments: React.FC<EsignPlanDocumentsProps> = () => {
  const history = useHistory()
  const match = useRouteMatch()
  const { t: translate } = useTranslation()

  const { planPeriod } = useParams<any>()
  const params = useQueryParams()
  const returnTo = params.get('returnTo')
  const legalDocumentsPath = match.path.substring(0, match.path.indexOf(SIGN_PLAN_DOCUMENTS_PATH))

  const [submiting, setSubmiting] = useState<boolean>(false)
  const [legalDocumentUrl, setLegalDocumentUrl] = useState<string | undefined>(undefined)
  const [legalDocumentContent, setLegalDocumentContent] = useState<string | undefined>(undefined)
  const [externalLegalDocument, setExternalLegalDocument] = useState<boolean>(false)

  const { reloadUser } = useLoginUserV2()
  const { data: legalDocument } = useQueryRequestV2({
    queryFn: () => legalDocumentsStateApi(planPeriod),
  })
  const { sendQuery: sendGetPreviewLegalDocument } = useQueryRequestV2<string>()
  const { sendCommand: sendSignDocument, isLoading: isCommandLoading } = useCommandRequestV2(({}) =>
    eSignDocumentsApi(planPeriod),
  )

  useEffect(() => {
    ;(async () => {
      if (!legalDocument) return

      if (legalDocument.signed) {
        history.push(legalDocumentsPath)
      }

      setLegalDocumentUrl(legalDocument.legalDocumentFileUrl)
      setExternalLegalDocument(legalDocument.externalResource)

      if (!legalDocument.externalResource) {
        const documentContentResponse = await sendGetPreviewLegalDocument(() =>
          previewLegalDocumentsApi(legalDocument.legalDocumentFileUrl),
        )
        setLegalDocumentContent(documentContentResponse)
      }
    })()
  }, [legalDocument])

  const eSignDocument = async () => {
    setSubmiting(true)
    const response = await sendSignDocument({})

    if (response.completed) {
      await reloadUser()

      setSubmiting(false)

      Notification.success(translate('admin-dashboard.legal-documents.esign-alert-title'))

      history.push(returnTo === 'RenewalTasks' ? `${ROUTE_PATHS.RENEWAL_TASKS}` : `${ROUTE_PATHS.ADMIN_DASHBOARD_PATH}`)
    } else {
      setSubmiting(false)
    }
  }

  return (
    <div className="e_sign_document_content">
      <div className="document">
        <Skeleton active loading={(legalDocumentUrl && externalLegalDocument) === undefined}>
          {legalDocumentUrl && (
            <iframe
              src={externalLegalDocument ? legalDocumentUrl : null}
              srcDoc={externalLegalDocument ? null : legalDocumentContent}
              frameBorder={0}
              width="100%"
              height="100%"
              scrolling={externalLegalDocument ? 'no' : 'yes'}
              allowFullScreen={false}
            ></iframe>
          )}
        </Skeleton>
      </div>

      <div className="actions pt-16 pb-16">
        <Button
          className="e_sign_button"
          type="primary"
          htmlType="button"
          icon="fa fa-file-signature"
          loading={submiting}
          onClick={eSignDocument}
          disabled={legalDocumentUrl === undefined}
        >
          E-sign plan documents
        </Button>
      </div>
    </div>
  )
}

export default ESignPlanDocuments
